import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import maplibregl, { type Map as MapInstance } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

import { useMapExpansion } from "../../../components/map/useMapExpansion";
import { useMapSnapshot } from "../../../components/map/useMapSnapshot";
import { mapStyles } from "../../../components/map/createBaseMap";
import { MapShell } from "../../../components/map/MapShell";
import { useBaseMapInstance } from "../../../components/map/useBaseMapInstance";
import { mapFrameClasses } from "../../../components/map/mapFrameClasses";
import { useMapLotsData } from "../hooks/useMapLotsData";
import { sanitizeBBox } from "../utils/bbox";
import { openLotPopup } from "../utils/lotPopup";
import { fetchMapExtent, type MapLotsResponse } from "../api/lots";
import { createLogger } from "../../../utils/logger";
import { useThemeMode } from "../../../providers/ThemeProvider";
import { ArrowClockwise, DownloadSimple, WarningCircle } from "@phosphor-icons/react";
import { defaultIconProps } from "../../shared/ui/icons";

type LotMapSectionProps = {
  accessToken: string | null;
  workspaceIds?: string[];
  workspaceNames?: Record<string, string>;
  snapshotFileBase: string;
  mapIdPrefix?: string;
  onLotNavigate?: (workspaceId: string, lotId: string) => void;
  onDataUpdate?: (data: MapLotsResponse) => void;
  className?: string;
};

const defaultCenter: [number, number] = [121.0, 13.0];
const defaultZoom = 6;
const debounceMs = 220;

const fabButtonClasses = mapFrameClasses.fabButton;
const fabSpinClasses = mapFrameClasses.fabSpin;
const snapshotErrorClasses = mapFrameClasses.snapshotError;
const placeholderClasses = "text-sm font-semibold text-slate-100";
const errorBannerClasses =
  "mb-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300";
const exceededBannerClasses =
  "mb-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/30 dark:text-amber-100";

const emptyCollection = {
  type: "FeatureCollection" as const,
  features: [],
};

export function LotMapSection({
  accessToken,
  workspaceIds,
  workspaceNames,
  snapshotFileBase,
  mapIdPrefix = "lot-map",
  onLotNavigate,
  onDataUpdate,
  className,
}: LotMapSectionProps) {
  const { t } = useTranslation();
  const mapInstanceRef = useRef<MapInstance | null>(null);
  const selectedLotIdRef = useRef<string | null>(null);
  const activePopupRef = useRef<maplibregl.Popup | null>(null);
  const debounceRef = useRef<number | undefined>(undefined);
  const hasFittedRef = useRef(false);
  const hasFittedLotsRef = useRef(false);
  const skipNextMoveRef = useRef<number>(0);
  const zoomNudgeAppliedRef = useRef(false);
  const initialExtentRequestedRef = useRef(false);
  const lastRequestKeyRef = useRef<string | null>(null);
  const [isInitialEasePlaying, setIsInitialEasePlaying] = useState(false);
  const logger = useMemo(() => createLogger("LotMapSection"), []);
  const { theme } = useThemeMode();

  const {
    containerRef: mapContainerRef,
    status: mapStatus,
  } = useBaseMapInstance({
    style: theme === "dark" ? mapStyles.dark : mapStyles.light,
    mapOptions: { center: defaultCenter, zoom: defaultZoom },
    mapRefOverride: mapInstanceRef,
  });

  const {
    isExpanded: isMapExpanded,
    toggle: toggleMapExpansion,
  } = useMapExpansion();

  const {
    saveSnapshot,
    isSavingSnapshot,
    snapshotError,
  } = useMapSnapshot({
    mapRef: mapInstanceRef,
    getFileName: () => `${snapshotFileBase}-snapshot`,
    getErrorMessage: (err) =>
      err instanceof Error
        ? err.message
        : t("workspaces.lot_detail.map_save_error"),
    fallbackError: t("workspaces.lot_detail.map_export_error"),
    blobErrorMessage: t("workspaces.lot_detail.map_export_failed"),
  });

  const {
    data: mapData,
    isLoading,
    error,
    fetchData,
    setError,
    hasLoaded,
  } = useMapLotsData({
    accessToken,
    workspaceIds,
    messages: {
      defaultError: t("workspaces.lots.map.load_error"),
      viewportTooLarge: t("workspaces.lots.map.viewport_too_large"),
    },
  });

  useEffect(() => {
    if (onDataUpdate) {
      onDataUpdate(mapData);
    }
    logger.debug("Map data updated", {
      lots: mapData?.lots?.length ?? 0,
      clusters: mapData?.clusters?.length ?? 0,
      exceeded: mapData?.exceeded_max_results ?? false,
    });
  }, [logger, mapData, onDataUpdate]);

  const workspaceNamesMap = useMemo(
    () => workspaceNames ?? {},
    [workspaceNames],
  );

  const hasData =
    (mapData?.lots?.length ?? 0) > 0 || (mapData?.clusters?.length ?? 0) > 0;
  const loadingStageLabel = useMemo(() => {
    if (!mapStatus.isMapReady) {
      return t("workspaces.lots.map.loading_map", "Preparing map…");
    }
    if (!mapStatus.hasRenderedTiles) {
      return t("workspaces.lots.map.loading_tiles", "Loading map tiles…");
    }
    if (isLoading) {
      return t("workspaces.lots.map.loading_lots", "Loading lots…");
    }
    return null;
  }, [isLoading, mapStatus.hasRenderedTiles, mapStatus.isMapReady, t]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      if (activePopupRef.current) {
        activePopupRef.current.remove();
        activePopupRef.current = null;
      }
      hasFittedRef.current = false;
    };
  }, []);

  const updateSelection = useCallback(
    (nextId: string | null) => {
      const map = mapInstanceRef.current;
      if (!map) return;

      const polygonSourceId = `${mapIdPrefix}-polygons`;
      if (selectedLotIdRef.current && selectedLotIdRef.current !== nextId) {
        map.setFeatureState(
          { source: polygonSourceId, id: selectedLotIdRef.current },
          { selected: false },
        );
      }
      if (!nextId) {
        selectedLotIdRef.current = null;
        return;
      }
      map.setFeatureState({ source: polygonSourceId, id: nextId }, { selected: true });
      selectedLotIdRef.current = nextId;
    },
    [mapIdPrefix],
  );

  const scheduleFetch = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map || !accessToken) return;

    const bounds = map.getBounds();
    const center = map.getCenter();
    let west = bounds.getWest();
    let east = bounds.getEast();
    let south = bounds.getSouth();
    let north = bounds.getNorth();

    if (east < west) {
      east += 360;
    }

    const width = Math.max(east - west, 0.001);
    const height = Math.max(north - south, 0.001);
    const maxSpan = 80;
    const spanLng = Math.min(width / 2, maxSpan / 2);
    const spanLat = Math.min(height / 2, maxSpan / 2);

    west = center.lng - spanLng;
    east = center.lng + spanLng;
    south = center.lat - spanLat;
    north = center.lat + spanLat;

    const clamped = sanitizeBBox(west, south, east, north);

    setError(null);
    const zoom = map.getZoom();
    const nextZoom = Number.isFinite(zoom) ? Math.max(0, Math.round(zoom)) : 0;

    const bbox = `${clamped.west},${clamped.south},${clamped.east},${clamped.north}`;
    const requestKey = `${bbox}|${nextZoom}`;
    if (lastRequestKeyRef.current === requestKey) {
      logger.debug("Skipping duplicate map lots fetch", { bbox, zoom: nextZoom });
      return;
    }
    lastRequestKeyRef.current = requestKey;

    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      logger.debug("Cleared pending map request debounce");
    }
    debounceRef.current = window.setTimeout(() => {
      logger.debug("Scheduling map lots fetch", {
        bbox,
        zoom: nextZoom,
        size: {
          width: map.getContainer().clientWidth,
          height: map.getContainer().clientHeight,
        },
      });
      fetchData({
        bbox,
        zoom: nextZoom,
        clientWidth: map.getContainer().clientWidth,
        clientHeight: map.getContainer().clientHeight,
        preferClusters: true,
      });
    }, debounceMs);
  }, [accessToken, fetchData, logger, setError]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapStatus.isMapReady || initialExtentRequestedRef.current) {
      return;
    }
    initialExtentRequestedRef.current = true;

    const loadExtent = async () => {
      if (!accessToken) {
        scheduleFetch();
        return;
      }
      try {
        const extent = await fetchMapExtent(accessToken, {
          workspace_ids: workspaceIds,
        });
        if (extent?.bounds) {
          const bounds = new maplibregl.LngLatBounds();
          bounds.extend([extent.bounds.min_lng, extent.bounds.min_lat]);
          bounds.extend([extent.bounds.max_lng, extent.bounds.max_lat]);
          if (!bounds.isEmpty()) {
            logger.debug("Applying initial extent fit", {
              west: bounds.getWest(),
              east: bounds.getEast(),
              south: bounds.getSouth(),
              north: bounds.getNorth(),
            });
            skipNextMoveRef.current = 1;
            map.fitBounds(bounds, { padding: 60 });
            hasFittedRef.current = true;
            hasFittedLotsRef.current = true;
            scheduleFetch();
            return;
          }
        }
        logger.debug("Initial extent not available, using current viewport");
      } catch (err) {
        logger.warn("Failed to load initial extent", err);
      }
      scheduleFetch();
    };

    void loadExtent();
  }, [accessToken, logger, mapStatus.isMapReady, scheduleFetch, workspaceIds]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapStatus.isMapReady) return;

    const hasLots = (mapData?.lots?.length ?? 0) > 0;
    const hasClusters = (mapData?.clusters?.length ?? 0) > 0;

    const shouldFit =
      (!hasFittedRef.current && (hasLots || hasClusters)) ||
      (hasLots && !hasFittedLotsRef.current);

    if (shouldFit) {
      const bounds = new maplibregl.LngLatBounds();
      if (mapData?.bounds) {
        bounds.extend([mapData.bounds.min_lng, mapData.bounds.min_lat]);
        bounds.extend([mapData.bounds.max_lng, mapData.bounds.max_lat]);
      }
      const addBBox = (bbox: [number, number, number, number], buffer = 0) => {
        const [minLng, minLat, maxLng, maxLat] = bbox;
        const width = maxLng - minLng;
        const height = maxLat - minLat;
        if (!Number.isFinite(width) || !Number.isFinite(height)) return false;
        if (width <= 0 || height <= 0) return false;
        if (width > 50 || height > 50) return false;
        bounds.extend([minLng - buffer, minLat - buffer]);
        bounds.extend([maxLng + buffer, maxLat + buffer]);
        return true;
      };

      if (mapData.clusters) {
        mapData.clusters.forEach((cluster) => {
          const bbox = cluster.bbox;
          const validBBox =
            Array.isArray(bbox) && bbox.length === 4 && bbox.every((v) => Number.isFinite(v));
          if (validBBox) {
            addBBox(bbox as [number, number, number, number], 0.5);
          } else if (cluster.coordinates) {
            const [lng, lat] = cluster.coordinates;
            addBBox([lng - 0.5, lat - 0.5, lng + 0.5, lat + 0.5]);
          }
        });
      }
      if (mapData.lots) {
        mapData.lots.forEach((lot) => {
          const bbox = lot.bbox;
          const validBBox =
            Array.isArray(bbox) && bbox.length === 4 && bbox.every((v) => Number.isFinite(v));
          if (validBBox) {
            addBBox(bbox as [number, number, number, number], 0.1);
          } else if (Array.isArray(lot.centroid)) {
            const [lng, lat] = lot.centroid;
            addBBox([lng - 0.1, lat - 0.1, lng + 0.1, lat + 0.1]);
          }
        });
      }

      const mapBounds = map.getBounds();
      const shrinkLng = (mapBounds.getEast() - mapBounds.getWest()) * 0.05;
      const shrinkLat = (mapBounds.getNorth() - mapBounds.getSouth()) * 0.05;
      const viewportContainsData =
        !bounds.isEmpty() &&
        bounds.getWest() >= mapBounds.getWest() + shrinkLng &&
        bounds.getEast() <= mapBounds.getEast() - shrinkLng &&
        bounds.getSouth() >= mapBounds.getSouth() + shrinkLat &&
        bounds.getNorth() <= mapBounds.getNorth() - shrinkLat;

      if (!bounds.isEmpty() && !viewportContainsData) {
        hasFittedRef.current = true;
        hasFittedLotsRef.current = hasLots;
        skipNextMoveRef.current = 1;
        logger.debug("Fitting map to data bounds", {
          west: bounds.getWest(),
          east: bounds.getEast(),
          south: bounds.getSouth(),
          north: bounds.getNorth(),
        });
        map.fitBounds(bounds, { padding: 60, duration: 420 });
        if (!zoomNudgeAppliedRef.current) {
          zoomNudgeAppliedRef.current = true;
          setIsInitialEasePlaying(true);
          window.setTimeout(() => {
            const currentZoom = map.getZoom();
            const targetZoom = Math.min(
              currentZoom + 0.45,
              (typeof map.getMaxZoom === "function" ? map.getMaxZoom() : 18) - 0.25,
            );
            skipNextMoveRef.current = 1;
            map.easeTo({
              zoom: targetZoom,
              duration: 360,
              easing: (t) => t * t,
            });
            window.setTimeout(() => setIsInitialEasePlaying(false), 420);
          }, 520);
        }
      } else {
        hasFittedRef.current = true;
        hasFittedLotsRef.current = hasLots;
      }
    }

    const handleMoveEnd = () => {
      if (skipNextMoveRef.current > 0) {
        skipNextMoveRef.current -= 1;
        logger.debug("Skipping moveend/zoomend triggered by programmatic fit");
        return;
      }

      scheduleFetch();
    };
    const resetSkipOnUser = () => {
      skipNextMoveRef.current = 0;
    };
    map.on("moveend", handleMoveEnd);
    map.on("zoomend", handleMoveEnd);
    map.on("dragstart", resetSkipOnUser);
    map.on("zoomstart", resetSkipOnUser);

    return () => {
      map.off("moveend", handleMoveEnd);
      map.off("zoomend", handleMoveEnd);
      map.off("dragstart", resetSkipOnUser);
      map.off("zoomstart", resetSkipOnUser);
    };
  }, [logger, mapData, mapStatus.isMapReady, scheduleFetch]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    map.resize();
  }, [isMapExpanded]);

  const popupForLot = useCallback(
    (lotId: string, lngLat: maplibregl.LngLatLike) => {
      const map = mapInstanceRef.current;
      if (!map) return;
      const lot = mapData?.lots?.find((item) => item.id === lotId);
      if (!lot) return;
      if (activePopupRef.current) {
        activePopupRef.current.remove();
        activePopupRef.current = null;
      }
      activePopupRef.current = openLotPopup({
        map,
        lngLat,
        t,
        lotId: lot.id,
        title: lot.title || t("workspaces.lots.untitled"),
        area: lot.computed_area_m2 ?? null,
        workspaceId: lot.workspace_id,
        workspaceName: workspaceNamesMap[lot.workspace_id],
        onViewDetails:
          onLotNavigate && lot.workspace_id
            ? () => onLotNavigate(lot.workspace_id, lot.id)
            : undefined,
        closeButton: true,
        onClose: () => {
          updateSelection(null);
          activePopupRef.current = null;
        },
      });
    },
    [mapData?.lots, onLotNavigate, t, updateSelection, workspaceNamesMap],
  );

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapStatus.isMapReady) return;

    const polygonSourceId = `${mapIdPrefix}-polygons`;
    const labelSourceId = `${mapIdPrefix}-labels`;
    const clusterSourceId = `${mapIdPrefix}-clusters`;

    const ensureSources = () => {
      if (!map.getSource(polygonSourceId)) {
        map.addSource(polygonSourceId, { type: "geojson", promoteId: "id", data: emptyCollection });
      }
      if (!map.getSource(labelSourceId)) {
        map.addSource(labelSourceId, { type: "geojson", data: emptyCollection });
      }
      if (!map.getSource(clusterSourceId)) {
        map.addSource(clusterSourceId, { type: "geojson", promoteId: "id", data: emptyCollection });
      }
    };

    const ensureLayers = () => {
      if (!map.getLayer(`${mapIdPrefix}-polygons-fill`)) {
        map.addLayer({
          id: `${mapIdPrefix}-polygons-fill`,
          source: polygonSourceId,
          type: "fill",
          paint: {
            "fill-color": [
              "case",
              ["boolean", ["feature-state", "selected"], false],
              "rgba(59, 130, 246, 0.36)",
              "rgba(14, 165, 233, 0.24)",
            ],
            "fill-outline-color": "rgba(59, 130, 246, 0.7)",
          },
        });
      }
      if (!map.getLayer(`${mapIdPrefix}-polygons-outline`)) {
        map.addLayer({
          id: `${mapIdPrefix}-polygons-outline`,
          source: polygonSourceId,
          type: "line",
          paint: {
            "line-color": "rgba(59, 130, 246, 0.8)",
            "line-width": 2,
          },
        });
      }
      if (!map.getLayer(`${mapIdPrefix}-labels-layer`)) {
        map.addLayer({
          id: `${mapIdPrefix}-labels-layer`,
          source: labelSourceId,
          type: "symbol",
          layout: {
            "text-field": ["get", "title"],
            "text-size": 13,
            "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold", "sans-serif"],
          },
          paint: {
            "text-color": "#0f172a",
            "text-halo-color": "rgba(255,255,255,0.96)",
            "text-halo-width": 2.2,
            "text-halo-blur": 0.6,
          },
        });
      }
      if (!map.getLayer(`${mapIdPrefix}-clusters-circles`)) {
        map.addLayer({
          id: `${mapIdPrefix}-clusters-circles`,
          source: clusterSourceId,
          type: "circle",
          paint: {
            "circle-color": [
              "interpolate",
              ["linear"],
              ["get", "point_count"],
              2,
              "rgba(14, 165, 233, 0.18)",
              20,
              "rgba(59, 130, 246, 0.38)",
              100,
              "rgba(22, 163, 74, 0.48)",
            ],
            "circle-radius": [
              "interpolate",
              ["linear"],
              ["get", "point_count"],
              2,
              14,
              20,
              22,
              50,
              30,
              100,
              38,
            ],
            "circle-stroke-width": 1.6,
            "circle-stroke-color": "rgba(59, 130, 246, 0.9)",
            "circle-blur": 0.25,
          },
        });
      }
      if (!map.getLayer(`${mapIdPrefix}-clusters-labels`)) {
        map.addLayer({
          id: `${mapIdPrefix}-clusters-labels`,
          source: clusterSourceId,
          type: "symbol",
          layout: {
            "text-field": ["get", "point_count"],
            "text-size": 13,
            "text-font": ["Open Sans Semibold", "Arial Unicode MS Bold", "sans-serif"],
          },
          paint: {
            "text-color": "#0b172a",
            "text-halo-color": "rgba(255,255,255,0.98)",
            "text-halo-width": 2.2,
            "text-halo-blur": 0.6,
          },
        });
      }
    };

    ensureSources();
    ensureLayers();

    const polygonFeatures = (mapData?.lots ?? [])
      .filter((lot) => Array.isArray(lot.preview_polygon) && lot.preview_polygon.length > 0)
      .map((lot) => ({
        type: "Feature" as const,
        id: lot.id,
        geometry: { type: "Polygon" as const, coordinates: lot.preview_polygon },
        properties: {
          id: lot.id,
          title: lot.title || t("workspaces.lots.untitled"),
          workspace_id: lot.workspace_id,
          area: lot.computed_area_m2 ?? null,
        },
      }));

    const labelFeatures = (mapData?.lots ?? []).map((lot) => ({
      type: "Feature" as const,
      id: `${lot.id}-label`,
      geometry: {
        type: "Point" as const,
        coordinates: lot.centroid,
      },
      properties: {
        id: lot.id,
        title: lot.title || t("workspaces.lots.untitled"),
      },
    }));

    const clusterFeatures = (mapData?.clusters ?? []).map((cluster) => ({
      type: "Feature" as const,
      id: cluster.id,
      geometry: {
        type: "Point" as const,
        coordinates: cluster.coordinates,
      },
      properties: {
        ...cluster,
      },
    }));

    const polygonSource = map.getSource(polygonSourceId) as maplibregl.GeoJSONSource;
    const labelSource = map.getSource(labelSourceId) as maplibregl.GeoJSONSource;
    const clusterSource = map.getSource(clusterSourceId) as maplibregl.GeoJSONSource;

    polygonSource.setData({
      type: "FeatureCollection",
      features: polygonFeatures,
    });
    labelSource.setData({
      type: "FeatureCollection",
      features: labelFeatures,
    });
    clusterSource.setData({
      type: "FeatureCollection",
      features: clusterFeatures,
    });

    const handleLotClick = (event: maplibregl.MapLayerMouseEvent) => {
      const feature = event.features?.[0];
      const lotId =
        typeof feature?.id === "string"
          ? feature.id
          : typeof feature?.properties?.id === "string"
            ? feature.properties.id
            : undefined;
      if (!lotId) return;
      updateSelection(lotId);
      popupForLot(lotId, event.lngLat);
    };

    const handleClusterClick = (event: maplibregl.MapLayerMouseEvent) => {
      const feature = event.features?.[0];
      const rawBBox = feature?.properties?.bbox as [number, number, number, number] | undefined;
      const coords = feature?.geometry?.type === "Point" ? feature.geometry.coordinates : undefined;
      const suggestedZoomProp = feature?.properties?.suggested_zoom as number | undefined;

      const bbox =
        Array.isArray(rawBBox) && rawBBox.length === 4
          ? rawBBox.map((v) => Number(v)) as [number, number, number, number]
          : undefined;

      const bounds = new maplibregl.LngLatBounds();

      if (bbox && bbox.every((v) => Number.isFinite(v))) {
        bounds.extend([bbox[0], bbox[1]]);
        bounds.extend([bbox[2], bbox[3]]);
      } else if (Array.isArray(coords)) {
        const [lng, lat] = coords as [number, number];
        const buffer = 0.35;
        bounds.extend([lng - buffer, lat - buffer]);
        bounds.extend([lng + buffer, lat + buffer]);
      }

      if (bounds.isEmpty()) return;

      skipNextMoveRef.current = 1;
      const targetZoom =
        typeof suggestedZoomProp === "number" && Number.isFinite(suggestedZoomProp)
          ? Math.max(8, Math.min(16, suggestedZoomProp))
          : undefined;

      if (
        coords &&
        Array.isArray(coords) &&
        typeof coords[0] === "number" &&
        typeof coords[1] === "number" &&
        targetZoom
      ) {
        map.easeTo({
          center: [coords[0], coords[1]],
          zoom: targetZoom,
          padding: { top: 48, bottom: 48, left: 48, right: 48 },
          duration: 400,
        });
      } else {
        map.fitBounds(bounds, { padding: 56, maxZoom: targetZoom ?? 30 });
      }
    };

    const handleLotEnter = () => {
      map.getCanvas().style.cursor = "pointer";
    };
    const handleLotLeave = () => {
      map.getCanvas().style.cursor = "";
    };
    const handleClusterEnter = () => {
      map.getCanvas().style.cursor = "pointer";
    };
    const handleClusterLeave = () => {
      map.getCanvas().style.cursor = "";
    };

    map.off("click", `${mapIdPrefix}-polygons-fill`, handleLotClick);
    map.on("click", `${mapIdPrefix}-polygons-fill`, handleLotClick);
    map.off("mouseenter", `${mapIdPrefix}-polygons-fill`, handleLotEnter);
    map.on("mouseenter", `${mapIdPrefix}-polygons-fill`, handleLotEnter);
    map.off("mouseleave", `${mapIdPrefix}-polygons-fill`, handleLotLeave);
    map.on("mouseleave", `${mapIdPrefix}-polygons-fill`, handleLotLeave);

    map.off("click", `${mapIdPrefix}-clusters-circles`, handleClusterClick);
    map.on("click", `${mapIdPrefix}-clusters-circles`, handleClusterClick);
    map.off("mouseenter", `${mapIdPrefix}-clusters-circles`, handleClusterEnter);
    map.on("mouseenter", `${mapIdPrefix}-clusters-circles`, handleClusterEnter);
    map.off("mouseleave", `${mapIdPrefix}-clusters-circles`, handleClusterLeave);
    map.on("mouseleave", `${mapIdPrefix}-clusters-circles`, handleClusterLeave);

    return () => {
      map.off("click", `${mapIdPrefix}-polygons-fill`, handleLotClick);
      map.off("mouseenter", `${mapIdPrefix}-polygons-fill`, handleLotEnter);
      map.off("mouseleave", `${mapIdPrefix}-polygons-fill`, handleLotLeave);
      map.off("click", `${mapIdPrefix}-clusters-circles`, handleClusterClick);
      map.off("mouseenter", `${mapIdPrefix}-clusters-circles`, handleClusterEnter);
      map.off("mouseleave", `${mapIdPrefix}-clusters-circles`, handleClusterLeave);
    };
  }, [mapData?.clusters, mapData?.lots, mapIdPrefix, mapStatus.isMapReady, popupForLot, t, updateSelection]);

  const mapWrapperClasses = useMemo(
    () => className ?? "",
    [className],
  );
  const mapCanvasClasses = useMemo(
    () =>
      [
        "transition-opacity duration-500",
        mapStatus.hasRenderedTiles ? "opacity-100" : "opacity-0",
        isInitialEasePlaying ? "animate-[pulse_0.6s_ease-in-out]" : "",
      ]
        .filter(Boolean)
        .join(" "),
    [isInitialEasePlaying, mapStatus.hasRenderedTiles],
  );
  const showLoadingPill = Boolean(loadingStageLabel);

  return (
    <div className="flex flex-col gap-3">
      {error ? <div className={errorBannerClasses}>{error}</div> : null}
      {mapData?.exceeded_max_results ? (
        <div className={exceededBannerClasses}>
          <div className="flex items-center gap-2">
            <WarningCircle {...defaultIconProps} aria-hidden />
            <span>
              {t(
                "workspaces.lots.map.zoom_in_prompt",
                "Too many properties here. Zoom in to see details.",
              )}
            </span>
          </div>
        </div>
      ) : null}
      <MapShell
        mapContainerRef={mapContainerRef}
        mapClassName={mapCanvasClasses}
        wrapperClassName={mapWrapperClasses}
        status={mapStatus}
        loadingLabel={showLoadingPill ? loadingStageLabel ?? undefined : undefined}
        isExpanded={isMapExpanded && hasData && mapStatus.isMapReady}
        onToggle={toggleMapExpansion}
        toggleLabels={{
          expandButton: t("workspaces.lot_detail.map_expand_button"),
          collapseButton: t("workspaces.lot_detail.map_collapse_button"),
          expandAria: t("workspaces.lot_detail.map_expand_aria"),
          collapseAria: t("workspaces.lot_detail.map_collapse_aria"),
        }}
        disableToggle={!hasData || !mapStatus.isMapReady}
        fabAriaLabel={t("workspaces.lot_detail.map_controls_aria")}
        onZoomIn={() => {
          const map = mapInstanceRef.current;
          if (!map) return;
          const zoom = map.getZoom();
          if (!Number.isFinite(zoom)) return;
          map.easeTo({ zoom: zoom + 1, duration: 200 });
        }}
        onZoomOut={() => {
          const map = mapInstanceRef.current;
          if (!map) return;
          const zoom = map.getZoom();
          if (!Number.isFinite(zoom)) return;
          map.easeTo({ zoom: zoom - 1, duration: 200 });
        }}
        zoomAria={{
          in: t("workspaces.lots.map.zoom_in", "Zoom in"),
          out: t("workspaces.lots.map.zoom_out", "Zoom out"),
        }}
        fabContent={
          <button
            type="button"
            className={fabButtonClasses}
            onClick={() => saveSnapshot()}
            disabled={isSavingSnapshot || !mapStatus.isMapReady}
            aria-label={t("workspaces.lot_detail.map_save_button")}
            title={t("workspaces.lot_detail.map_save_button")}
          >
            {isSavingSnapshot ? (
              <ArrowClockwise
                {...defaultIconProps}
                className={fabSpinClasses}
                aria-hidden
              />
            ) : (
              <DownloadSimple {...defaultIconProps} aria-hidden />
            )}
          </button>
        }
        placeholder={
          hasLoaded && !hasData && !isLoading ? (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-4 text-center">
              <p className={placeholderClasses}>{t("workspaces.lots.map.empty_state")}</p>
            </div>
          ) : null
        }
      />
      {snapshotError ? <p className={snapshotErrorClasses}>{snapshotError}</p> : null}
    </div>
  );
}
